import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil, filter, take } from 'rxjs';
import { Chart, ChartData, ChartOptions, registerables } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { DailyGlanceService, DailyGlanceDto } from '../../services/daily-glance.service';
import { AgentService, ZoneDto } from '../../services/agent.service';
import { AuthService } from '../../services/auth.service';

Chart.register(...registerables);

type DatePreset = 'today' | 'yesterday' | 'custom';

@Component({
  selector: 'app-daily-glance',
  standalone: true,
  imports: [CommonModule, FormsModule, BaseChartDirective],
  templateUrl: './daily-glance.component.html',
})
export class DailyGlanceComponent implements OnInit, OnDestroy {
  loading = false;
  errorMessage = '';
  data: DailyGlanceDto | null = null;

  selectedPreset: DatePreset = 'today';
  customDate = '';
  selectedZone = '';
  zones: ZoneDto[] = [];

  presets: { id: DatePreset; label: string; icon: string }[] = [
    { id: 'today', label: 'Today', icon: 'fa-calendar-day' },
    { id: 'yesterday', label: 'Yesterday', icon: 'fa-calendar-minus' },
    { id: 'custom', label: 'Custom', icon: 'fa-sliders-h' },
  ];

  readonly doughnutOptions: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    animation: { duration: 0 },
    plugins: {
      // Legend intentionally off: it's drawn inside the same canvas, so a side legend claims
      // layout space and pushes the ring off-center relative to the canvas box, throwing off
      // the centered total overlay. The color-to-category mapping is already on the labeled,
      // icon-badged stat tiles next to each chart, so nothing is lost by dropping it here.
      legend: { display: false },
    },
  };

  scheduledStatusChartData: ChartData<'doughnut'> = { labels: [], datasets: [] };
  channelChartData: ChartData<'doughnut'> = { labels: [], datasets: [] };
  agentDutyChartData: ChartData<'doughnut'> = { labels: [], datasets: [] };

  private destroy$ = new Subject<void>();

  constructor(
    private dailyGlanceService: DailyGlanceService,
    private agentService: AgentService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.customDate = this.getIstDateString(0);

    this.authService.authReady$
      .pipe(
        filter((ready) => ready === true),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.loadZones();
        this.loadGlance();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadZones(): void {
    this.agentService
      .getAllZones()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (zones: ZoneDto[]) => {
          this.zones = zones || [];
        },
        error: (err: unknown) => {
          console.error('Failed to load zones', err);
        },
      });
  }

  selectPreset(preset: DatePreset): void {
    this.selectedPreset = preset;
    if (preset !== 'custom') {
      this.loadGlance();
    }
  }

  onCustomDateChange(): void {
    if (this.customDate) {
      this.loadGlance();
    }
  }

  onZoneChange(): void {
    this.loadGlance();
  }

  loadGlance(): void {
    const dateStr =
      this.selectedPreset === 'today'
        ? this.getIstDateString(0)
        : this.selectedPreset === 'yesterday'
          ? this.getIstDateString(-1)
          : this.customDate;

    if (!dateStr) {
      return;
    }

    const { fromDate, toDate } = this.toUtcDayRange(dateStr);

    this.loading = true;
    this.errorMessage = '';

    this.dailyGlanceService
      .getDailyGlance({ fromDate, toDate, geoZoneIds: this.selectedZone || undefined })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: DailyGlanceDto) => {
          this.data = response;
          this.buildCharts(response);
          this.loading = false;
        },
        error: (err: unknown) => {
          console.error('Failed to load daily glance report', err);
          this.errorMessage = 'Failed to load the daily glance report. Please try again.';
          this.loading = false;
        },
      });
  }

  getZoneName(zoneId: string): string {
    const zone = this.zones.find((z) => z.id === zoneId);
    return zone ? zone.name : zoneId;
  }

  /** Rounded percentage of `value` out of `total`, or '0%' when there's nothing to divide by. */
  pct(value: number, total: number): string {
    if (!total) {
      return '0%';
    }
    return `${Math.round((value / total) * 100)}%`;
  }

  private buildCharts(data: DailyGlanceDto): void {
    const s = data.appointments.scheduledToday;
    this.scheduledStatusChartData = {
      labels: ['Fulfilled', 'Pending', 'Cancelled', 'Rejected', 'Rescheduled away'],
      datasets: [
        {
          data: [s.fulfilled, s.pendingOrConfirmed, s.cancelled, s.rejected, s.rescheduledAway],
          backgroundColor: ['#16a34a', '#f59e0b', '#dc2626', '#b91c1c', '#6b7280'],
          borderWidth: 0,
        },
      ],
    };

    const c = data.appointments.createdToday;
    this.channelChartData = {
      labels: ['Via ads', 'Via direct WhatsApp', 'Organic'],
      datasets: [
        {
          data: [c.convertedViaAds, c.convertedViaDirectWhatsApp, c.withoutWhatsApp],
          backgroundColor: ['#4f46e5', '#16a34a', '#9ca3af'],
          borderWidth: 0,
        },
      ],
    };

    const a = data.agents;
    this.agentDutyChartData = {
      labels: ['On duty', 'On leave'],
      datasets: [
        {
          data: [a.onDuty, a.onLeave],
          backgroundColor: ['#059669', '#f59e0b'],
          borderWidth: 0,
        },
      ],
    };
  }

  /**
   * Returns today (or today + offsetDays) as YYYY-MM-DD in IST, independent of the viewing
   * browser's own timezone — this is an India operations report, so "today" must mean the IST
   * calendar day even if an admin happens to be browsing from elsewhere.
   */
  private getIstDateString(offsetDays: number): string {
    const now = new Date();
    const istNow = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
    istNow.setDate(istNow.getDate() + offsetDays);
    const year = istNow.getFullYear();
    const month = String(istNow.getMonth() + 1).padStart(2, '0');
    const day = String(istNow.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Converts a calendar date (YYYY-MM-DD) to UTC-midnight-anchored bounds for that day.
   * NOT IST-aware on purpose: Appointment.Date/ScrapCollection.Date are stored and filtered
   * elsewhere in this app (see appointments-list's `new Date(dateStr).toISOString()`) as plain
   * UTC midnight of the picked calendar label, with no IST offset applied. Matching that
   * convention here — even though it's arguably imprecise — is what actually lines up with the
   * stored data; an IST-correct +05:30 shift looks more "correct" but silently pulls in the
   * wrong 5.5-hour window relative to every other appointment date filter in the app.
   *
   * fromDate and toDate are deliberately the SAME instant, not next-day midnight: the backend
   * (DailyGlanceController) snaps each one independently via DateOnly.FromDateTime(...) to that
   * value's own calendar day, then reconstructs day-start/day-end itself. Sending toDate as
   * "tomorrow" made the controller extract tomorrow's date and build a two-day window instead
   * of one -- matching Appointments List's own from/to-on-the-same-day convention avoids that.
   */
  private toUtcDayRange(dateStr: string): { fromDate: string; toDate: string } {
    const from = new Date(`${dateStr}T00:00:00.000Z`);
    return { fromDate: from.toISOString(), toDate: from.toISOString() };
  }
}
